import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenantContext } from "../../../contexts/TenantContext";
import { useLocations } from "../../../hooks/useLocations";
import { useDistributionRules } from "../../../hooks/useDistributionRules";
import { useIntegrations } from "../../../contexts/IntegrationContext";
import Icon from "../../../components/AppIcon";

const LocationManagement = () => {
  const { isGlobalAdmin, isTenantAdmin } = useAuth();
  const { selectedTenant, currentTenantId, needsTenantSelection } = useTenantContext();
  
  // Only fetch data when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  const {
    locations,
    loading,
    error,
    fetchLocations,
    createLocation,
    updateLocation,
  deleteLocation,
    deactivateLocation,
  } = useLocations({ 
    autoFetch: shouldFetchData,
    tenantId: currentTenantId // Pass current tenant ID
  });
  
  const { rules } = useDistributionRules({ autoFetch: shouldFetchData });
  const { hasHrIntegration, hasPosIntegration, integrations } =
    useIntegrations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    county: "",
    postCode: "",
    country: "United Kingdom",
    phoneNumber: "",
    email: "",
    managerName: "",
    timeZone: "Europe/London",
    isActive: true,
    isTroncEnabled: true,
    defaultDistributionRuleId: "",
    // Integration identifiers (will be populated dynamically based on configured integrations)
    hrIdentifier: "",
    posIdentifier: "",
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      county: "",
      postCode: "",
      country: "United Kingdom",
      phoneNumber: "",
      email: "",
      managerName: "",
      timeZone: "Europe/London",
      isActive: true,
      isTroncEnabled: true,
      defaultDistributionRuleId: "",
      hrIdentifier: "",
      posIdentifier: "",
    });
    setEditingLocation(null);
    setFormError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || "",
      code: location.code || "",
      addressLine1: location.addressLine1 || "",
      addressLine2: location.addressLine2 || "",
      city: location.city || "",
      county: location.county || "",
      postCode: location.postCode || "",
      country: location.country || "United Kingdom",
      phoneNumber: location.phoneNumber || "",
      email: location.email || "",
      managerName: location.managerName || "",
      timeZone: location.timeZone || "Europe/London",
      isActive: location.isActive ?? true,
      isTroncEnabled: location.isTroncEnabled ?? true,
      defaultDistributionRuleId: location.defaultDistributionRuleId || "",
      hrIdentifier: location.hrIdentifier || "",
      posIdentifier: location.posIdentifier || "",
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      if (editingLocation) {
        await updateLocation(editingLocation.id, formData);
      } else {
        await createLocation(formData);
      }
      closeModal();
      fetchLocations();
    } catch (err) {
      setFormError(err.message || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLocation(id);
      setDeleteConfirm(null);
    } catch (err) {
      alert(err.message || "Failed to delete location");
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivateLocation(id);
    } catch (err) {
      alert(err.message || "Failed to deactivate location");
    }
  };

  // Refetch when tenant changes
  useEffect(() => {
    if (shouldFetchData) {
      fetchLocations();
    }
  }, [selectedTenant?.id, shouldFetchData]);

  if (!isTenantAdmin) {
    return (
    <div className="p-6">
        <div className="card p-8 text-center">
  <Icon
            name="ShieldAlert"
     size={48}
            className="mx-auto mb-4 text-warning"
        />
   <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
     <p className="text-muted-foreground">
      You don't have permission to manage locations.
        </p>
 </div>
      </div>
    );
  }

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Location Management - Rocket Tronc</title>
        </Helmet>
      <div className="p-6 max-w-7xl mx-auto">
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
   </div>
         <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
   to view and manage their locations.
       </p>
    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
 <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
   </div>
        </div>
  </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Location Management - {selectedTenant?.name || "Rocket Tronc"}</title>
      </Helmet>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Location Management</h1>
            <p className="text-muted-foreground mt-1">
              {selectedTenant 
     ? `Viewing ${selectedTenant.name} - Manage business locations and TRONC collection points`
              : "Manage your business locations and TRONC collection points"
        }
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="btn btn-primary flex items-center gap-2"
          >
            <Icon name="Plus" size={20} />
            Add Location
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="card p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading locations...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && locations.length === 0 && (
          <div className="card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="MapPin" size={32} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Locations Yet</h2>
            <p className="text-muted-foreground mb-4">
              Get started by adding your first business location.
            </p>
            <button onClick={openCreateModal} className="btn btn-primary">
              <Icon name="Plus" size={20} className="mr-2" />
              Add Your First Location
            </button>
          </div>
        )}

        {/* Locations Grid */}
        {!loading && locations.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map((location) => (
              <div
                key={location.id}
                className="card hover:shadow-md transition-shadow"
              >
                <div className="card-content p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${location.isActive ? "bg-primary/10" : "bg-muted"}`}
                      >
                        <Icon
                          name="Building2"
                          size={20}
                          className={
                            location.isActive
                              ? "text-primary"
                              : "text-muted-foreground"
                          }
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold">{location.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {location.code}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!location.isActive && (
                        <span className="badge badge-neutral text-xs">
                          Inactive
                        </span>
                      )}
                      {location.isTroncEnabled && location.isActive && (
                        <span className="badge badge-success text-xs">
                          TRONC
                        </span>
                      )}
                    </div>
                  </div>

                  {(location.city || location.postCode) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Icon name="MapPin" size={14} />
                      <span>
                        {[location.city, location.postCode]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  )}

                  {location.managerName && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Icon name="User" size={14} />
                      <span>{location.managerName}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <Icon name="Users" size={14} />
                      <span>{location.employeeCount || 0} employees</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="FileText" size={14} />
                      <span>{location.troncRecordCount || 0} records</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <button
                      onClick={() => openEditModal(location)}
                      className="btn btn-sm btn-ghost flex-1"
                    >
                      <Icon name="Pencil" size={16} className="mr-1" />
                      Edit
                    </button>
                    {location.isActive ? (
                      <button
                        onClick={() => handleDeactivate(location.id)}
                        className="btn btn-sm btn-ghost text-warning"
                      >
                        <Icon name="PowerOff" size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(location)}
                        className="btn btn-sm btn-ghost text-error"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">
                    {editingLocation ? "Edit Location" : "Add New Location"}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-muted rounded-lg"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {formError && (
                  <div className="alert alert-error">
                    <Icon name="AlertCircle" size={20} />
                    <span>{formError}</span>
                  </div>
                )}

                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Location Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="input w-full"
                        placeholder="e.g., London Bridge"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Code *
                      </label>
                      <input
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleInputChange}
                        required
                        className="input w-full"
                        placeholder="e.g., LDN-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Manager Name
                      </label>
                      <input
                        type="text"
                        name="managerName"
                        value={formData.managerName}
                        onChange={handleInputChange}
                        className="input w-full"
                        placeholder="Location manager"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Address
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Address Line 1
                      </label>
                      <input
                        type="text"
                        name="addressLine1"
                        value={formData.addressLine1}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        name="addressLine2"
                        value={formData.addressLine2}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        County
                      </label>
                      <input
                        type="text"
                        name="county"
                        value={formData.county}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Post Code
                      </label>
                      <input
                        type="text"
                        name="postCode"
                        value={formData.postCode}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="input w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Settings
                  </h3>
                  <div className="space-y-3">
                    {/* Default Distribution Rule */}
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Default Distribution Rule
                      </label>
                      <select
                        name="defaultDistributionRuleId"
                        value={formData.defaultDistributionRuleId || ""}
                        onChange={handleInputChange}
                        className="input w-full"
                      >
                        <option value="">
                          No default rule (use tenant default)
                        </option>
                        {rules
                          ?.filter((r) => r.isActive)
                          .map((rule) => (
                            <option key={rule.id} value={rule.id}>
                              {rule.name} -{" "}
                              {rule.type === "Equal" || rule.type === 0
                                ? "Equal Split"
                                : "By Job Role"}
                            </option>
                          ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        The default rule to use for TRONC distributions at this
                        location
                      </p>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="checkbox"
                      />
                      <div>
                        <span className="font-medium">Active</span>
                        <p className="text-sm text-muted-foreground">
                          Location is operational and visible
                        </p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isTroncEnabled"
                        checked={formData.isTroncEnabled}
                        onChange={handleInputChange}
                        className="checkbox"
                      />
                      <div>
                        <span className="font-medium">TRONC Enabled</span>
                        <p className="text-sm text-muted-foreground">
                          Allow TRONC collection at this location
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Integration Identifiers - Dynamic based on configured integrations */}
                {(hasHrIntegration || hasPosIntegration) && (
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Integration Identifiers
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      These identifiers link this location to your integrated
                      systems
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {hasHrIntegration && (
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            {integrations.hr} HR System ID
                          </label>
                          <input
                            type="text"
                            name="hrIdentifier"
                            value={formData.hrIdentifier}
                            onChange={handleInputChange}
                            className="input w-full"
                            placeholder={`Location ID in ${integrations.hr}`}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            The location identifier from your {integrations.hr}{" "}
                            system
                          </p>
                        </div>
                      )}
                      {hasPosIntegration && (
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            {integrations.pos} POS System ID
                          </label>
                          <input
                            type="text"
                            name="posIdentifier"
                            value={formData.posIdentifier}
                            onChange={handleInputChange}
                            className="input w-full"
                            placeholder={`Location ID in ${integrations.pos}`}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            The location identifier from your {integrations.pos}{" "}
                            system
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary"
                  >
                    {saving ? (
                      <>
                        <span className="spinner-sm mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Icon name="Check" size={20} className="mr-2" />
                        {editingLocation
                          ? "Update Location"
                          : "Create Location"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-error" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Delete Location</h3>
                  <p className="text-sm text-muted-foreground">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <p className="mb-6">
                Are you sure you want to permanently delete{" "}
                <strong>{deleteConfirm.name}</strong>? All associated data will
                be lost.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="btn btn-error"
                >
                  <Icon name="Trash2" size={20} className="mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LocationManagement;
